import React, { useContext, useEffect, useRef, useState } from "react";
// redux
import { useSelector } from "react-redux";
import { selectWindowDataList } from "@src/@slice/interWindowSlice";
// user
import { UserContext } from "@src/App";
// router
import { useLocation, useParams, useNavigate } from "react-router-dom";
// hooks
import { useInterWindow } from "@src/utils/hooks/useInterWindow";
import { useBroadcastChannel } from "@src/utils/hooks/useBroadcastChannel";
// review
import { ReviewProviderData } from "./Provider";
import { useReviewActions } from "./useReviewActions";
import { useModalFixed } from "@src/utils/hooks/useModalFixed";
// components
import CommunityActionModal from "@src/components/community/CommunityActionModal";
import { ReviewItem, ReviewCommentItem } from "./Item";
import { ReviewCommentWrite } from "./Comment";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
import PullToRefresh from "@src/components/common/loading/PullToRefresh";
import AppHeader from "@src/components/common/appHeader/AppHeader";
import { Wrap } from "@src/shared/src";

// 리뷰 상세보기
const ReviewDetailSection = () => {
  // user
  const user = useContext(UserContext);

  // interWindow list
  const ForWindowDataList = useSelector(selectWindowDataList);

  // 상대창(sender) 정보
  const [senderId, setSenderId] = useState<string[]>([]);

  // 이미지 모달 열기 설정
  const { openWindow: openReviewImage } = useInterWindow({
    id: "reviewDetailSection",
    receiverId: "image",
    needResetData: false,
    callBackWhenBackToOriginalWindow: () => {
      setReviewImageData(null);
    },
  });

  // 리뷰 상세 데이터 설정
  useEffect(() => {
    if (ForWindowDataList.reviewDetailSection?.data) {
      setPartner(ForWindowDataList.reviewDetailSection?.data?.partner);
      setSenderId(ForWindowDataList.reviewDetailSection?.senderIdList);
      setReviewDataDetail(ForWindowDataList.reviewDetailSection?.data?.reviewData);
    } else {
      if (user?.me?.uuid && !reviewDataDetail) {
        setShowReviewDetailLoader(true);
        setRefreshFlag(true);
      }
    }
  }, [ForWindowDataList?.reviewDetailSection, user?.me?.uuid]);

  // context
  const {
    setPartner,
    reviewDataDetail,
    setReviewDataDetail,
    communityModalData,
    reviewImageData,
    commentList,
    refreshFlag,
    setRefreshFlag,
    setReviewImageData,
  } = useContext(ReviewProviderData).data;

  // router
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid: partnerUUid, reviewUuid: reviewUUid } = useParams<{
    uuid: string;
    reviewUuid: string;
  }>();

  // AOS용 커뮤니티 모달 닫기
  useEffect(() => {
    if (location && !location?.state) {
      setCommunityModalOpen(false);
    }
  }, [location]);

  // iOS 체크
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // actions
  const { reviewDetailCall, reviewCommentCall } = useReviewActions();

  // data ref
  const reviewDataDetailRef = useRef(null);

  // community modal
  const [communityModalOpen, setCommunityModalOpen] = useState(false);

  // 리뷰 상세 로더 표시 여부
  const [showReviewDetailLoader, setShowReviewDetailLoader] = useState(false);

  // 마운트 여부
  const reviewDetailIsMount = useRef(false);

  // 스크롤 컴포넌트 - PTR 적용
  const scrollComponent = useRef<{
    wrap: HTMLDivElement;
    contents: HTMLDivElement;
    actions: HTMLDivElement;
    calcProperties: () => void;
  }>(null);

  // 당겨서 새로고침 비활성화
  // const pullToRefreshDisable = useRef(false);
  const [pullToRefreshDisable, setPullToRefreshDisable] = useState(true);

  // 차단 처리된 리뷰일 경우 리뷰 리스트 새로고침 후 창 닫기
  const [blockedCheck, setBlockedCheck] = useState(false);

  // 상대창(sender)으로부터 데이터 수신 처리
  const { broadcastMessage: broadcastReviewMessage } = useBroadcastChannel(
    "review",
    (e: MessageEvent) => {
      if (e?.data?.type === "sendUpdatedDataToReceiver") {
        const { newReviewData } = e.data;
        setReviewDataDetail(newReviewData);
      }
    },
  );

  // 닫기 신호 (리뷰 상세 닫기 신호)
  const closeSignal = useRef(false);

  // 상대창(sender)에 업데이트 필요 전달 여부
  const notifyStatus = useRef(false);

  // 상대창(sender)에 업데이트 필요 전달 시 (데이터는 전달X, 리스트 갱신하도록 신호 전달하는 부분)
  useEffect(() => {
    if (notifyStatus?.current) {
      notifyStatus.current = false;
      broadcastReviewMessage({
        type: "notifySenderForDataUpdate",
        id: "reviewDetailSection",
        senderId: senderId,
      });
      if (closeSignal.current) {
        // 리뷰 상세창 닫기 신호가 있을 경우 리뷰상세창 닫음
        closeSignal.current = false;
        window.location.href = `h2mwbell://webview/close`;
      }
    }
  }, [notifyStatus?.current, senderId]);

  // 리뷰 상세 데이터 호출 + 코멘트 리스트 가져오기 처리
  const getReviewDetailData = async (needListUpdate?: boolean) => {
    // 코멘트 불러오는 응답속도 느려서 분리함
    reviewCommentCall(reviewUUid).then(() => {
      setPullToRefreshDisable(false); // PTR은 코멘트까지 불러온 후 활성화되도록
    });

    const detailResult = await reviewDetailCall(reviewUUid);
    if (detailResult) {
      if (needListUpdate) {
        notifyStatus.current = true; // 리뷰 리스트 갱신 필요 신호 전달
      }
      if (detailResult) {
        setReviewDataDetail({
          ...reviewDataDetail,
          reviewData: detailResult,
        });
      }
      setShowReviewDetailLoader(false);
    }
  };

  // 새로고침 함수 (PTR)
  const refreshFunction = () => {
    return new Promise<boolean>((resolve, reject) => {
      setRefreshFlag(true);
      resolve(true);
    });
  };

  // 새로고침 플래그에 따라 새로고침 처리 (당겨서 새로고침 등)
  useEffect(() => {
    if (refreshFlag) {
      setRefreshFlag(false);
      getReviewDetailData();
    }
  }, [refreshFlag]);

  // 코멘트 리스트, 리뷰데이터 변경에 따라 처리
  useEffect(() => {
    if (commentList !== null && reviewDataDetail !== null) {
      setShowReviewDetailLoader(false); // 코멘트 리스트, 리뷰데이터 호출 완료 시 로더 제거
    } else {
      return; // 코멘트 리스트, 리뷰데이터가 없을 경우 리턴
    }
    // 코멘트 리스트가 변경되었을 경우 리뷰데이터의 코멘트 카운트 변경
    if (
      commentList &&
      reviewDataDetailRef?.current?.reviewData &&
      commentList.length !== reviewDataDetailRef.current.reviewData.replyCnt
    ) {
      setReviewDataDetail((prev) => {
        return {
          ...reviewDataDetailRef.current,
          reviewData: { ...prev.reviewData, replyCnt: commentList.length },
        };
      });
      return;
    }
  }, [commentList]);

  // 리뷰 코멘트 데이터 호출
  useEffect(() => {
    if (user?.me?.uuid && !refreshFlag) {
      reviewCommentCall(reviewUUid).finally(() => {
        setPullToRefreshDisable(false);
      });
    }
  }, [user?.me?.uuid]);

  // 리뷰 이미지 데이터에 따라 포토 모달 새창 열기
  useEffect(() => {
    if (reviewImageData?.id && reviewDataDetail?.reviewData?.uuid && reviewDetailIsMount.current) {
      openReviewImage(
        {
          photoData: reviewImageData,
        },
        {
          url: `/image`,
          hidetop: true,
          hidesafearea: false,
        },
      );
    }
  }, [reviewImageData?.id]);

  // 커뮤니티모달 열림
  useEffect(() => {
    if (communityModalData?.id) {
      setCommunityModalOpen(true);

      if (!isiOS) {
        // AOS일 경우 뒤로가기 할 수 있도록 처리
        navigate(".", { state: { communityModal: true } });
      }
    }
  }, [communityModalData?.id]);

  // 차단 처리된 리뷰일 경우 리뷰 리스트 새로고침 후 창 닫기
  useEffect(() => {
    if (blockedCheck) {
      setBlockedCheck(false);
      closeSignal.current = true;
      notifyStatus.current = true;
    }
  }, [blockedCheck, senderId]);

  // 리뷰 리스트로 변경된 리뷰 상세 데이터 전달
  useEffect(() => {
    if (!reviewDataDetail?.reviewData) return;
    reviewDataDetailRef.current = reviewDataDetail;

    // 최초 리뷰 데이터 전달 시에는 하단 코드 실행되지 않도록 리턴
    if (!reviewDetailIsMount.current) {
      reviewDetailIsMount.current = true;
      return;
    }

    // 최초 데이터 전달 이후 리뷰데이터 바뀌었을 경우 상대창(sender)에 업데이트 데이터 전달
    if (reviewDetailIsMount.current && reviewDataDetail?.reviewData) {
      broadcastReviewMessage({
        type: "sendUpdatedDataToSender",
        newReviewData: reviewDataDetail,
        id: "reviewDetailSection",
        senderId: senderId,
      });

      // 리뷰 상세창 닫기 신호가 있을 경우 리뷰상세창 닫음
      if (closeSignal.current) {
        closeSignal.current = false;
        window.location.href = `h2mwbell://webview/close`;
      }
    }
  }, [reviewDataDetail?.reviewData]);

  // 스크롤 막기
  const { modalWrapRef, modalContentRef } = useModalFixed({
    isShow: true,
    needBackgroundFixed: true,
    overflowHidden: false,
  });

  const [isCommentMode, setIsCommentMode] = useState(null);

  return (
    <Wrap
      ref={scrollComponent}
      className="review-detail-modal"
      viewportResize={false}
      fullHeight={true}
      headerComponent={
        <>
          <AppHeader type="stack" title="상세보기" scrollHide={false} />
        </>
      }
      actionsWrapProps={{
        fade: false,
        fadeHeight: { before: 0, after: 0 },
        style: {
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
        },
      }}
      contentsWrapProps={{
        style: {
          paddingBottom: 0,
        },
      }}
      contentsComponent={{
        contents: (
          <>
            {/* 리뷰 상세 */}
            {refreshFunction && !pullToRefreshDisable && !isCommentMode && (
              <>
                <PullToRefresh
                  refreshFunction={refreshFunction}
                  parentElement={scrollComponent?.current?.wrap}
                  element={scrollComponent?.current?.contents?.querySelector(".review-list-wrap")}
                  disable={pullToRefreshDisable}
                />
              </>
            )}
            <div className="review-list-wrap review-basic-wrap" ref={modalWrapRef}>
              {reviewDataDetail?.reviewData && (
                <>
                  <div
                    className={`review-item`}
                    key={`review-item-${reviewDataDetail.id}-${0}`}
                    ref={modalContentRef}
                  >
                    <ReviewItem list={reviewDataDetail.reviewData} viewType="list-detail" />
                    <ReviewCommentItem
                      list={commentList}
                      commentCount={commentList?.length || reviewDataDetail?.reviewData?.replyCnt}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        ),
        actions: (
          <>
            {reviewDataDetail?.reviewData && (
              <ReviewCommentWrite
                isCommentModeCheck={(status: boolean) => {
                  setIsCommentMode(status);
                }}
                scrollComponent={scrollComponent?.current}
              />
            )}
          </>
        ),
        extra: (
          <>
            {/* 커뮤니티 모달(신고/차단) */}
            {communityModalData && (
              <CommunityActionModal
                isShow={!!communityModalOpen}
                category={communityModalData.category}
                communityUuid={communityModalData.communityUuid}
                customerUuid={communityModalData.customerUuid}
                clearData={(result) => {
                  if (!isiOS) {
                    navigate(-1);
                  }
                  setCommunityModalOpen(false);
                  setPullToRefreshDisable(false);
                  if (result) {
                    setBlockedCheck(true);
                  }
                }}
              />
            )}
            {showReviewDetailLoader && <LoadingSpinner />}
          </>
        ),
      }}
    />
  );
};

export default ReviewDetailSection;
